import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getAllContent, saveContent, publishContent, deleteContent, getSettings, updateSettings, ContentSection, getTeams, getFixtures, Team } from '../api/client';
import { getSectionsByPage, getSchemaById, SectionSchema } from '../contentSchemas';
import { useAuth } from '../contexts/AuthContext';
import BodyField from '../components/BodyField';
import { richTextAllowed } from '../utils/richText';
import ImageUploader from '../components/ImageUploader';
import { getEmbedUrl } from '../utils/videoEmbed';
import './ContentEditor.css';
import { overlayClose } from '../utils/overlayClose';

// Home page blocks a club can move. Hero stays first and "Ready to Join?" last.
// Mirrors lambda/lib/home-order.ts and the website's lib/homeOrder.ts.
const HOME_ORDER_BLOCKS: Array<{ key: string; label: string; icon: string }> = [
  { key: 'news', label: 'Next match and news', icon: '📰' },
  { key: 'matches', label: 'Matchday (the week\'s games and where a team stands)', icon: '📅' },
  { key: 'about', label: 'About and club numbers', icon: 'ℹ️' },
  { key: 'video', label: 'Featured video', icon: '🎬' },
  { key: 'shop', label: 'Shop', icon: '🛍️' },
  { key: 'affiliations', label: 'Affiliations', icon: '🏅' },
  { key: 'sponsors', label: 'Sponsors', icon: '🤝' },
];

const DEFAULT_HOME_ORDER = HOME_ORDER_BLOCKS.map(b => b.key);

function fullHomeOrder(saved: string[] | undefined): string[] {
  const out = (saved || []).filter((k, i, a) => DEFAULT_HOME_ORDER.includes(k) && a.indexOf(k) === i);
  const full = [...out, ...DEFAULT_HOME_ORDER.filter(k => !out.includes(k))];
  // Matchday came after clubs had saved an order: it goes under the news, not to the bottom.
  if (!(saved || []).includes('matches')) {
    full.splice(full.indexOf('matches'), 1);
    full.splice(full.indexOf('news') + 1, 0, 'matches');
  }
  return full;
}

// Pages a Free club genuinely cannot have. News is plan-gated (features.news is
// set from the plan). Shop is NOT: Free includes 5 products at a 5% fee, which
// is what the landing page sells, so telling a Free club to upgrade to edit
// their shop page contradicted the pricing and blocked something they own.
const LOCKED_PAGES = ['News'];

function ContentEditor() {
  const { plan, features } = useAuth();
  const [allContent, setAllContent] = useState<ContentSection[]>([]);
  const [selectedPage, setSelectedPage] = useState<string | null>(null);
  const [selectedSection, setSelectedSection] = useState<string>('');
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [originalData, setOriginalData] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Section visibility (churn feedback 2026-07-15: "features I didn't need
  // I couldn't hide"). Instant-save toggles; the server subtracts hidden
  // sections from the public site + sitemap. Mirrors lambda/lib/features.ts.
  const HIDEABLE_SECTIONS: Array<{ key: string; label: string; icon: string }> = [
    { key: 'teams', label: 'Teams', icon: '⚽' },
    { key: 'join', label: 'Join Us', icon: '📝' },
    { key: 'news', label: 'News', icon: '📰' },
    { key: 'events', label: 'Events', icon: '📅' },
    { key: 'shop', label: 'Shop', icon: '🛍️' },
    { key: 'gallery', label: 'Gallery', icon: '📸' },
    { key: 'documents', label: 'Documents', icon: '📄' },
    { key: 'homeGround', label: 'Home Ground', icon: '🏟️' },
  ];
  // Options for a select whose choices are the club's own data. Loaded once,
  // lazily, only when a schema actually asks for them.
  const [teamOptions, setTeamOptions] = useState<{ value: string; label: string }[] | null>(null);
  // Loaded once on mount so the Next Match picker can offer the club's own
  // sides. Fixtures wait until that section is actually opened.
  const [clubTeams, setClubTeams] = useState<Team[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    getTeams()
      .then(ts => { if (!cancelled) setClubTeams(ts); })
      .catch(() => { if (!cancelled) setClubTeams(null); });
    return () => { cancelled = true; };
  }, []);

  const [hiddenFeatures, setHiddenFeatures] = useState<Record<string, boolean> | null>(null);
  const [visSavingKey, setVisSavingKey] = useState('');
  const [homeOrder, setHomeOrder] = useState<string[] | null>(null);
  const [orderSaving, setOrderSaving] = useState(false);

  const moveHomeBlock = (index: number, step: -1 | 1) => {
    if (!homeOrder) return;
    const next = [...homeOrder];
    const a = next[index];
    const b = next[index + step];
    if (a === undefined || b === undefined) return;
    next[index] = b;
    next[index + step] = a;
    saveHomeOrder(next);
  };

  const saveHomeOrder = async (next: string[]) => {
    if (!homeOrder) return;
    setHomeOrder(next);            // optimistic
    setOrderSaving(true);
    try {
      await updateSettings({ homeOrder: next });
    } catch (err) {
      setHomeOrder(homeOrder);     // revert on failure
      setError(err instanceof Error ? err.message : 'Failed to save the home page order');
    } finally {
      setOrderSaving(false);
    }
  };

  const toggleSectionVisibility = async (key: string) => {
    if (!hiddenFeatures) return;
    const next = { ...hiddenFeatures };
    if (next[key]) delete next[key];
    else next[key] = true;
    setHiddenFeatures(next);       // optimistic
    setVisSavingKey(key);
    try {
      await updateSettings({ hiddenFeatures: next });
    } catch (err) {
      setHiddenFeatures(hiddenFeatures); // revert on failure
      setError(err instanceof Error ? err.message : 'Failed to update section visibility');
    } finally {
      setVisSavingKey('');
    }
  };

  useEffect(() => {
    loadAllContent();
    getSettings()
      .then(s => {
        setHiddenFeatures(s.hiddenFeatures || {});
        setHomeOrder(fullHomeOrder(s.homeOrder));
      })
      .catch(() => { setHiddenFeatures(null); setHomeOrder(null); }); // cards hide if settings unavailable
  }, []);

  useEffect(() => {
    if (selectedSection) {
      loadSectionContent(selectedSection);
    }
  }, [selectedSection, allContent]);

  const loadAllContent = async () => {
    try {
      setIsLoading(true);
      const draftContent = await getAllContent('draft');
      setAllContent(draftContent);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load content');
    } finally {
      setIsLoading(false);
    }
  };

  // Only sides with a future fixture are offered. Picking a team that will
  // never have a match would leave the club with a section that never appears
  // and no clue why.
  useEffect(() => {
    const wanted = getSchemaById(selectedSection)?.fields
      .some(f => f.optionsSource === 'teamsWithFixtures');
    if (!wanted || teamOptions || !clubTeams) return;
    let cancelled = false;
    (async () => {
      try {
        const teams = clubTeams;
        const fixtures = await getFixtures();
        if (cancelled) return;
        const today = new Intl.DateTimeFormat('en-CA', {
          timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit',
        }).format(new Date());
        const withFixtures = new Set(
          fixtures.filter(f => f.status === 'scheduled' && f.date >= today && f.teamId)
            .map(f => f.teamId as string),
        );
        const playing = teams.filter(x => withFixtures.has(x.id));

        // The list has to explain itself. A club with one team has this handled
        // for them and the setting is ignored, and a club whose sides have no
        // fixtures yet needs to know that rather than see an empty dropdown.
        const off = { value: 'off', label: "Don't show this section" };
        if (teams.length <= 1) {
          // One team is its own answer, so this is simply on or off. On is the
          // default: leaving it alone keeps the block, which is what 81 clubs
          // already have.
          setTeamOptions([{ value: '', label: 'Show my next match' }, off]);
        } else if (!playing.length) {
          setTeamOptions([{ value: '', label: 'No team has a fixture yet' }, off]);
        } else {
          setTeamOptions([
            { value: '', label: 'Select a team' },
            { value: 'any', label: 'Whichever team plays next' },
            ...playing.map(x => ({ value: x.id, label: x.name })),
            off,
          ]);
        }
      } catch {
        if (!cancelled) setTeamOptions([{ value: '', label: 'Could not load teams' }]);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedSection, teamOptions, clubTeams]);

  const loadSectionContent = (sectionId: string) => {
    const existing = allContent.find(c => c.section === sectionId);
    const content = existing?.content || {};
    const data: Record<string, string> = {};
    const schema = getSchemaById(sectionId);
    if (schema) {
      for (const field of schema.fields) {
        data[field.key] = content[field.key] != null ? String(content[field.key]) : '';
      }
    }
    setFormData(data);
    setOriginalData(data);
  };

  const handleFieldChange = (key: string, value: string) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const handleSaveDraft = async () => {
    setError('');
    setSuccessMessage('');
    setIsSaving(true);
    try {
      await saveContent(selectedSection, formData);
      setSuccessMessage('Draft saved successfully');
      setOriginalData(formData);
      await loadAllContent();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save draft');
    } finally {
      setIsSaving(false);
    }
  };

  const handlePublish = async () => {
    setError('');
    setSuccessMessage('');
    setIsPublishing(true);
    try {
      await saveContent(selectedSection, formData);
      await publishContent(selectedSection);
      setSuccessMessage('Content published - changes are now live on the website');
      setOriginalData(formData);
      await loadAllContent();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to publish content');
    } finally {
      setIsPublishing(false);
    }
  };

  const handleDelete = async () => {
    setError('');
    setSuccessMessage('');
    setIsDeleting(true);
    try {
      await deleteContent(selectedSection);
      setSuccessMessage('Content deleted - website will show default text');
      setShowDeleteConfirm(false);
      await loadAllContent();
      setFormData({});
      setOriginalData({});
      loadSectionContent(selectedSection);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete content');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleNavigateToPage = (pageName: string) => {
    setSelectedPage(pageName);
    setSelectedSection('');
    setError('');
    setSuccessMessage('');
  };

  const handleNavigateToSection = (sectionId: string) => {
    setSelectedSection(sectionId);
    setError('');
    setSuccessMessage('');
  };

  const handleBack = () => {
    if (selectedSection) {
      setSelectedSection('');
      setError('');
      setSuccessMessage('');
    } else if (selectedPage) {
      setSelectedPage(null);
      setError('');
      setSuccessMessage('');
    }
  };

  const hasChanges = JSON.stringify(formData) !== JSON.stringify(originalData);
  const hasAnyContent = Object.values(formData).some(v => v.trim() !== '');
  const schema = getSchemaById(selectedSection);

  // With features.registration on, the Join form's questions are edited on the
  // Registrations page instead — that is where someone is already thinking about
  // sign-ups. Two screens editing the same content is how one of them goes stale.
  const registrationOn = (features as any)?.registration === true;
  const MOVED_TO_REGISTRATIONS = ['join-form-visibility', 'join-form-custom'];
  const isMovedSection = registrationOn && MOVED_TO_REGISTRATIONS.includes(schema?.id || '');
  const visibleFields = schema?.fields || [];
  const sectionHasSavedContent = allContent.some(c => c.section === selectedSection);
  const pageGroups = getSectionsByPage().map(g => ({
    ...g,
    sections: g.sections.filter(sec =>
      !((features as any)?.registration === true && ['join-form-visibility', 'join-form-custom'].includes(sec.id))),
  })).filter(g => g.sections.length > 0);
  const isPageLocked = plan === 'free' && schema && LOCKED_PAGES.includes(schema.page);


  // Page icons for level 1
  const pageIcons: Record<string, string> = {
    'Home': '🏠',
    'Teams': '⚽',
    'News': '📰',
    'Home Ground': '📍',
    'Join Us': '🙋',
    'Shop': '🛍️',
    'Safeguarding': '🛡️',
    'Footer / Contact': '📬',
    'Site-wide': '🌐',
  };

  // Pages edits the words around each page. These point to where the page's
  // actual things are added, so nobody hunts for them here.
  const pageLinks: Record<string, Array<{ to: string; text: string; label: string }>> = {
    'Home': (features as any)?.fixtures
      ? [{ to: '/fixtures', text: 'Next Match shows your next game from Fixtures.', label: 'Fixtures' }]
      : [],
    'Teams': [{ to: '/teams', text: 'Teams, squads and coaches are added in Club Management.', label: 'Club Management' }],
    'News': [{ to: '/news', text: 'This edits the News page heading. Articles are written in News.', label: 'Write and edit articles' }],
    'Home Ground': [{ to: '/venues', text: 'Other grounds and match venues are added in Where We Play.', label: 'Where We Play' }],
    'Join Us': registrationOn
      ? [{ to: '/registrations', text: 'Sign-ups and the form questions are in Registrations.', label: 'Registrations' }]
      : [],
    'Shop': [
      { to: '/products', text: 'Products are added in Shop.', label: 'Products' },
      { to: '/orders', text: 'Orders come in here.', label: 'Orders' },
    ],
    'Safeguarding': [{ to: '/documents', text: 'Upload your policies in Documents, under Safeguarding.', label: 'Documents' }],
    'Footer / Contact': [{ to: '/settings', text: "The club's main contact details and social links are in Settings.", label: 'Settings' }],
  };

  // Count edited sections per page
  const getPageEditCount = (sections: SectionSchema[]): number => {
    return sections.filter(s => allContent.some(c => c.section === s.id)).length;
  };

  if (isLoading) {
    return <div className="loading">Loading content...</div>;
  }

  // ─── Breadcrumb ────────────────────────────────────────
  const breadcrumb = (
    <div className="ce-breadcrumb">
      <button
        className={`ce-breadcrumb-item ${!selectedPage ? 'active' : ''}`}
        onClick={() => { setSelectedPage(null); setSelectedSection(''); setError(''); setSuccessMessage(''); }}
      >
        Pages
      </button>
      {selectedPage && (
        <>
          <span className="ce-breadcrumb-sep">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>
          </span>
          <button
            className={`ce-breadcrumb-item ${!selectedSection ? 'active' : ''}`}
            onClick={() => { setSelectedSection(''); setError(''); setSuccessMessage(''); }}
          >
            {selectedPage}
          </button>
        </>
      )}
      {selectedSection && schema && (
        <>
          <span className="ce-breadcrumb-sep">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>
          </span>
          <span className="ce-breadcrumb-item active">{schema.name}</span>
        </>
      )}
    </div>
  );

  // ─── Level 1: Pages ────────────────────────────────────
  if (!selectedPage) {
    return (
      <div className="content-editor">
        <div className="page-header">
          <div>
            <h1 className="page-title">Pages</h1>
            <p className="page-subtitle">Edit website content — changes go live when you publish</p>
          </div>
        </div>

        <div className="ce-levels-container">
          <div className="ce-card">
            {pageGroups.map(({ page, sections }) => {
              const editCount = getPageEditCount(sections);
              const isLocked = plan === 'free' && LOCKED_PAGES.includes(page);
              return (
                <button
                  key={page}
                  className={`ce-row ${isLocked ? 'ce-row-locked' : ''}`}
                  onClick={() => !isLocked ? handleNavigateToPage(page) : undefined}
                >
                  <span className="ce-row-icon">{pageIcons[page] || '📄'}</span>
                  <div className="ce-row-content">
                    <span className="ce-row-title">{page}</span>
                    <span className="ce-row-subtitle">
                      {sections.length} section{sections.length !== 1 ? 's' : ''}
                      {editCount > 0 && ` · ${editCount} edited`}
                    </span>
                  </div>
                  {isLocked ? (
                    <span className="ce-row-badge ce-badge-locked">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                      Upgrade
                    </span>
                  ) : (
                    <span className="ce-row-chevron">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {hiddenFeatures !== null && (
            <div className="ce-card" style={{ marginTop: 16 }}>
              <div style={{ padding: '14px 16px 4px' }}>
                <strong>Show on website</strong>
                <p className="page-subtitle" style={{ margin: '4px 0 6px' }}>
                  Turn a section off to hide it from your public site (menu, pages
                  and Google). Your content is kept — turn it back on any time.
                </p>
              </div>
              {HIDEABLE_SECTIONS.map(({ key, label, icon }) => (
                <div className="ce-row" key={key} style={{ cursor: 'default' }}>
                  <span className="ce-row-icon">{icon}</span>
                  <div className="ce-row-content">
                    <span className="ce-row-title">{label}</span>
                    <span className="ce-row-subtitle">
                      {hiddenFeatures[key] ? 'Hidden from your website' : 'Visible on your website'}
                    </span>
                  </div>
                  <label className="checkbox-label" style={{ margin: 0 }}>
                    <input
                      type="checkbox"
                      checked={!hiddenFeatures[key]}
                      disabled={visSavingKey === key}
                      onChange={() => toggleSectionVisibility(key)}
                    />
                  </label>
                </div>
              ))}
            </div>
          )}

          {homeOrder !== null && (
            <div className="ce-card" style={{ marginTop: 16 }}>
              <div style={{ padding: '14px 16px 4px' }}>
                <strong>Home page order</strong>
                <p className="page-subtitle" style={{ margin: '4px 0 6px' }}>
                  Move sections up or down your home page. The hero is always first
                  and Ready to Join? always last. A section with nothing in it is skipped.
                </p>
              </div>
              <div className="ce-row" style={{ cursor: 'default' }}>
                <span className="ce-row-icon">🏠</span>
                <div className="ce-row-content">
                  <span className="ce-row-title">Hero</span>
                  <span className="ce-row-subtitle">Always first</span>
                </div>
              </div>
              {homeOrder.map((key, i) => {
                const block = HOME_ORDER_BLOCKS.find(b => b.key === key)!;
                return (
                  <div className="ce-row" key={key} style={{ cursor: 'default' }}>
                    <span className="ce-row-icon">{block.icon}</span>
                    <div className="ce-row-content">
                      <span className="ce-row-title">{block.label}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        aria-label={`Move ${block.label} up`}
                        disabled={orderSaving || i === 0}
                        onClick={() => moveHomeBlock(i, -1)}
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        aria-label={`Move ${block.label} down`}
                        disabled={orderSaving || i === homeOrder.length - 1}
                        onClick={() => moveHomeBlock(i, 1)}
                      >
                        ↓
                      </button>
                    </div>
                  </div>
                );
              })}
              <div className="ce-row" style={{ cursor: 'default' }}>
                <span className="ce-row-icon">✅</span>
                <div className="ce-row-content">
                  <span className="ce-row-title">Ready to Join?</span>
                  <span className="ce-row-subtitle">Always last</span>
                </div>
              </div>
              <div style={{ padding: '12px 16px', display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  disabled={orderSaving || homeOrder.every((k, i) => k === DEFAULT_HOME_ORDER[i])}
                  onClick={() => saveHomeOrder(DEFAULT_HOME_ORDER)}
                >
                  Reset to default
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ─── Level 2: Sections within a page ───────────────────
  const currentPageSections = pageGroups.find(g => g.page === selectedPage)?.sections || [];

  if (!selectedSection) {
    return (
      <div className="content-editor">
        <div className="page-header">
          <div>
            <button className="ce-back-btn" onClick={handleBack}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 18l-6-6 6-6"/></svg>
              Back
            </button>
            <h1 className="page-title">{selectedPage}</h1>
            <p className="page-subtitle">{currentPageSections.length} editable section{currentPageSections.length !== 1 ? 's' : ''}</p>
          </div>
        </div>

        {breadcrumb}

        <div className="ce-levels-container">
          {(pageLinks[selectedPage || ''] || []).length > 0 && (
            <div className="ce-note ce-page-links">
              {(pageLinks[selectedPage || ''] || []).map(l => (
                <p key={l.to}>
                  {l.text} <Link to={l.to} className="ce-note-link">{l.label} &rarr;</Link>
                </p>
              ))}
            </div>
          )}
          <div className="ce-card">
            {currentPageSections.map((section) => {
              const hasSaved = allContent.some(c => c.section === section.id);
              return (
                <button
                  key={section.id}
                  className="ce-row"
                  onClick={() => handleNavigateToSection(section.id)}
                >
                  <div className="ce-row-content">
                    <span className="ce-row-title">{section.name}</span>
                    <span className="ce-row-subtitle">{section.description}</span>
                  </div>
                  <div className="ce-row-end">
                    {hasSaved && <span className="ce-row-badge ce-badge-edited">Edited</span>}
                    <span className="ce-row-chevron">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // ─── Level 3: Edit form ────────────────────────────────
  return (
    <div className="content-editor">
      <div className="page-header">
        <div>
          <button className="ce-back-btn" onClick={handleBack}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 18l-6-6 6-6"/></svg>
            Back
          </button>
          <h1 className="page-title">{schema?.name || ''}</h1>
          <p className="page-subtitle">{schema?.description || ''}</p>
        </div>
      </div>

      {breadcrumb}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && schema && (
        <div className="modal-overlay" {...overlayClose(() => setShowDeleteConfirm(false))}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>Reset to Default</h3>
            <p>
              This will delete your custom content for &ldquo;{schema.name}&rdquo;.
              The website will show its default text instead.
            </p>
            <div className="modal-actions">
              <button onClick={() => setShowDeleteConfirm(false)} className="btn btn-outline">
                Cancel
              </button>
              <button onClick={handleDelete} className="btn btn-danger" disabled={isDeleting}>
                {isDeleting ? 'Deleting...' : 'Reset to Default'}
              </button>
            </div>
          </div>
        </div>
      )}

      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}

      <div className="ce-levels-container">
        {schema ? (
          isPageLocked ? (
            <div className="ce-card">
              <div className="locked-page">
                <div className="locked-icon">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                </div>
                <h2 className="locked-title">{schema.page} is a Premium Feature</h2>
                <p className="locked-desc">
                  Upgrade your plan to unlock the {schema.page} page and start editing its content.
                </p>
                <Link to="/upgrade" className="btn btn-primary">
                  Upgrade to Unlock
                </Link>
              </div>
            </div>
          ) : (
            <div className="ce-card ce-form-card">
              {/* Action buttons at top */}
              <div className="ce-form-actions">
                {sectionHasSavedContent && (
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="btn btn-outline btn-danger-outline btn-small"
                  >
                    Reset
                  </button>
                )}
                <button
                  onClick={handleSaveDraft}
                  className="btn btn-secondary btn-small"
                  disabled={isSaving || isImageUploading || !hasChanges}
                >
                  {isSaving ? 'Saving...' : isImageUploading ? 'Uploading image…' : 'Save Draft'}
                </button>
                <button
                  onClick={handlePublish}
                  className="btn btn-primary btn-small"
                  disabled={isPublishing || isImageUploading || (!hasChanges && !hasAnyContent)}
                >
                  {isPublishing ? 'Publishing...' : isImageUploading ? 'Uploading image…' : 'Publish'}
                </button>
              </div>

              {/* Form fields */}
              <div className="ce-form-fields">
                {isMovedSection && (
                  <div className="ce-note" style={{ marginBottom: 16 }}>
                    These questions now live on the <strong>Registrations</strong> page, alongside the
                    sign-ups they produce. Anything you change here will be overwritten from there.
                  </div>
                )}
                {visibleFields.map(field => (
                  <div key={field.key} className="form-group field-group">
                    <label htmlFor={field.key} className="form-label">
                      {field.label}
                    </label>
                    {field.hint && <p className="form-hint">{field.hint}</p>}

                    {field.type === 'image' ? (
                      <ImageUploader
                        currentImage={formData[field.key] || undefined}
                        onUpload={(url) => handleFieldChange(field.key, url)}
                        onUploadingChange={setIsImageUploading}
                        label=""
                        previewStyle={field.previewStyle}
                      />
                    ) : field.type === 'select' ? (
                      <select
                        id={field.key}
                        className="form-input"
                        value={formData[field.key] || ''}
                        onChange={e => handleFieldChange(field.key, e.target.value)}
                      >
                        {(field.optionsSource === 'teamsWithFixtures'
                          ? teamOptions ?? [{ value: '', label: 'Loading teams…' }]
                          : field.optionsSource === 'teams'
                            ? [{ value: '', label: 'Same team as Next Match' }, ...(clubTeams || []).map(t => ({ value: t.id, label: t.name }))]
                            : field.options ?? []
                        ).map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    ) : field.type === 'video' ? (
                      <div className="video-field">
                        <input
                          id={field.key}
                          type="text"
                          className="form-input"
                          value={formData[field.key] || ''}
                          onChange={e => handleFieldChange(field.key, e.target.value)}
                          placeholder="https://www.youtube.com/watch?v=... or https://vimeo.com/..."
                        />
                        {formData[field.key] && getEmbedUrl(formData[field.key] as string) && (
                          <div className="video-preview">
                            <iframe
                              src={getEmbedUrl(formData[field.key] as string)!}
                              allowFullScreen
                              title="Video preview"
                            />
                          </div>
                        )}
                      </div>
                    ) : field.type === 'textarea' && field.rich ? (
                      <BodyField
                        id={field.key}
                        value={formData[field.key] || ''}
                        rich={richTextAllowed(plan, features)}
                        compact
                        onChange={v => handleFieldChange(field.key, v)}
                        rows={4}
                        placeholder={field.placeholder}
                      />
                    ) : field.type === 'textarea' ? (
                      <textarea
                        id={field.key}
                        className="form-textarea"
                        value={formData[field.key] || ''}
                        onChange={e => handleFieldChange(field.key, e.target.value)}
                        rows={4}
                        placeholder={field.placeholder}
                      />
                    ) : (
                      <input
                        id={field.key}
                        type={field.type === 'number' ? 'number' : 'text'}
                        className="form-input"
                        value={formData[field.key] || ''}
                        onChange={e => handleFieldChange(field.key, e.target.value)}
                        placeholder={field.placeholder}
                      />
                    )}
                  </div>
                ))}
              </div>

              {/* Footer info */}
              <div className="ce-form-footer">
                {hasChanges && (
                  <p className="version-text unsaved">You have unsaved changes</p>
                )}
                {!sectionHasSavedContent && (
                  <p className="version-text">
                    No custom content yet &mdash; the website is showing default text.
                    Edit and publish to override it.
                  </p>
                )}
              </div>
            </div>
          )
        ) : (
          <div className="ce-card">
            <div className="empty-state">
              <p>Select a section to start editing.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ContentEditor;
